import { Filter } from '@/mstore/types/filterConstants';
import { getRowsPerPage } from '@/ui/layout/rows-per-page';
import Period, { CUSTOM_RANGE } from 'Types/app/period';
import { FilterCategory, FilterKey } from 'Types/filter/filterType';
import {
  filtersMap,
  generateFilterOptions,
  liveFiltersMap,
} from 'Types/filter/newFilter';
import { makeAutoObservable, runInAction } from 'mobx';

import {
  filterStore,
  projectStore,
  sessionStore,
  settingsStore,
} from 'App/mstore';
import { checkFilterValue } from 'App/mstore/types/filter';
import FilterItem from 'App/mstore/types/filterItem';
import SavedSearch, { ISavedSearch } from 'App/mstore/types/savedSearch';
import Search from 'App/mstore/types/search';
import { searchService, sessionService } from 'App/services';

/* The whole saved-search list is loaded in one request and shared by every
   consumer (session search, Data Management, the Issues capture layer). */
const SAVED_SEARCH_PAGE_SIZE = 200;

/* What the sessions/search handler actually binds. Applied at the request
   boundary only — toSearch() output is also what gets persisted as saved-search
   data and what the live-sessions endpoint receives, so it stays wider. */
const SEARCH_REQUEST_FIELDS = [
  'filters',
  'startTimestamp',
  'endTimestamp',
  'sort',
  'order',
  'eventsOrder',
  'limit',
  'page',
  'series',
  'bookmarked',
] as const;

const pickSearchRequestFields = (filter: Record<string, any>) =>
  SEARCH_REQUEST_FIELDS.reduce<Record<string, any>>((acc, key) => {
    if (filter[key] !== undefined) acc[key] = filter[key];
    return acc;
  }, {});

export const checkValues = (key: any, value: any) => {
  if (key === FilterKey.DURATION) {
    return value[0] === '' || value[0] === null ? [0, value[1]] : value;
  }
  return value.filter((i: any) => i !== '' && i !== null);
};

export const filterMap = (filter: any) => {
  const {
    category,
    value,
    key,
    name,
    operator,
    sourceOperator,
    source,
    custom,
    isEvent,
    filters,
    dataType,
    propertyOrder,
    autoCaptured,
  } = filter;

  return {
    name: name || key,
    type:
      name || (category === FilterCategory.METADATA ? FilterKey.METADATA : key),
    value: checkValues(key, value),
    operator: operator || 'is',
    dataType: dataType || 'string',
    propertyOrder: propertyOrder || (isEvent ? 'then' : undefined),
    custom,
    source:
      category === FilterCategory.METADATA ? key?.replace(/^_/, '') : source,
    sourceOperator,
    isEvent: Boolean(isEvent),
    autoCaptured: Boolean(autoCaptured),
    filters: filters ? filters.map(filterMap) : [],
  };
};

/** What decides a search's results, comparable across reloads of the same
    rules (values stringified, empty ones dropped). */
const ruleKey = (f: any): unknown => [
  f.name,
  Boolean(f.isEvent),
  f.operator || 'is',
  (Array.isArray(f.value) ? f.value : [f.value])
    .filter((v: unknown) => v != null && v !== '')
    .map(String),
  f.isEvent ? (f.propertyOrder ?? 'and') : null,
  (f.filters ?? []).map(ruleKey),
];

export const TAB_MAP: any = {
  all: { name: 'All', type: 'all' },
  sessions: { name: 'Sessions', type: 'sessions' },
  bookmarks: { name: 'Bookmarks', type: 'bookmarks' },
  recommendations: { name: 'Recommendations', type: 'recommendations' },
};

class SearchStore {
  list: SavedSearch[] = [];
  /** the window start of the list on screen; null until one has loaded */
  latestRequestTime: number | null = null;
  /** bumped whenever the list on screen changes: a count asked about an
      older list is not applied */
  private listGeneration = 0;
  latestList = [];
  alertMetricId: number | null = null;
  instance = new Search({
    startDate: Date.now() - 24 * 60 * 60 * 1000,
    endDate: Date.now(),
    filters: [],
    sort: 'startTs',
    order: 'desc',
    viewed: false,
    consoleLevel: '',
    eventsOrder: 'then',
  });
  savedSearch: ISavedSearch = new SavedSearch();
  /** the rules when the search became its saved segment */
  segmentRules: string | null = null;
  filterSearchList: any = {};
  currentPage = 1;
  activeTab = { name: 'All', type: 'all' };
  scrollY = 0;
  sessions = [];
  total: number = 0;
  latestSessionCount: number = 0;
  loadingFilterSearch = false;
  isSaving: boolean = false;
  activeTags: any[] = [];
  urlParsed: boolean = false;
  searchInProgress = false;

  /** Excluded from mobx: an observable proxy around it breaks abort(). */
  searchAbort: AbortController | null = null;
  savedSearchTotal = 0;
  /* untouched server rows behind `list` — Data Management builds its richer
     Segment objects from these (SavedSearch drops the count/capture fields) */
  savedSearchRaw: any[] = [];
  savedSearchLoaded = false;
  /* sessionsCount/usersCount are opt-in (withStats) — the sessions-list load
     skips them, so Data Management has to know whether the cache carries them */
  savedSearchHasStats = false;
  /* one in-flight request shared by every caller, so the app-level load in
     Router and the page-level loads don't each hit the endpoint */
  private savedSearchRequest: { key: string; promise: Promise<void> } | null =
    null;

  constructor() {
    // the shared request handle is plumbing, not view state
    makeAutoObservable(this, {
      savedSearchRequest: false,
      searchAbort: false,
      listGeneration: false,
    } as any);
  }

  resetFilters = () => {
    this.instance = new Search({
      startDate: Date.now() - 24 * 60 * 60 * 1000,
      endDate: Date.now(),
      filters: [],
      sort: 'startTs',
      order: 'desc',
      viewed: false,
      consoleLevel: '',
      eventsOrder: 'then',
    });
  };

  setUrlParsed() {
    this.urlParsed = true;
  }

  private get rulesKey() {
    return JSON.stringify([
      this.instance.eventsOrder,
      this.instance.filters.map(ruleKey),
    ]);
  }

  /** The search IS a saved segment: saved from it, or used from Segments. */
  get activeSegmentId(): string | null {
    const { searchId, isShare } = this.savedSearch;
    return searchId && !isShare ? String(searchId) : null;
  }

  get segmentEdited(): boolean {
    return this.segmentRules != null && this.rulesKey !== this.segmentRules;
  }

  /** The segment was saved with the rules on the page: take its record. */
  adoptSegment(saved: ISavedSearch) {
    this.savedSearch = new SavedSearch(saved);
    this.segmentRules = this.rulesKey;
  }

  /** Stop treating the search as its segment; the rules stay. */
  detachSegment() {
    this.savedSearch = new SavedSearch({});
    this.segmentRules = null;
  }

  get filterList() {
    return generateFilterOptions(filtersMap);
  }

  get filterListMobile() {
    return generateFilterOptions(filtersMap, true);
  }

  get filterListLive() {
    return generateFilterOptions(liveFiltersMap);
  }

  applySavedSearch(savedSearch: ISavedSearch) {
    this.savedSearch = new SavedSearch(savedSearch);

    const filtersData =
      savedSearch.data?.filters || savedSearch.filter?.filters || [];
    const searchData = savedSearch.data;
    const filters = filterStore.processFiltersFromData(filtersData);

    this.edit({
      filters: filters as any,
      startDate: searchData?.startTimestamp || this.instance.startDate,
      endDate: searchData?.endTimestamp || this.instance.endDate,
      sort: searchData?.sort || this.instance.sort,
      order: searchData?.order || this.instance.order,
      eventsOrder: searchData?.eventsOrder || this.instance.eventsOrder,
    });
    this.currentPage = 1;
    this.segmentRules = this.rulesKey;
  }

  /** Rules kept from an earlier search, back as editable chips. */
  restoreRules(filters: any[], eventsOrder: string) {
    this.detachSegment();
    this.edit({
      filters: filterStore.processFiltersFromData(filters) as any,
      eventsOrder,
    });
  }

  fetchSavedSearchList(
    page: number = 1,
    limit: number = SAVED_SEARCH_PAGE_SIZE,
    withStats = false,
  ): Promise<void> {
    const key = `${page}:${limit}:${withStats}`;
    if (this.savedSearchRequest?.key === key) {
      return this.savedSearchRequest.promise;
    }
    const promise = this.loadSavedSearchList(page, limit, withStats).finally(
      () => {
        if (this.savedSearchRequest?.key === key)
          this.savedSearchRequest = null;
      },
    );
    this.savedSearchRequest = { key, promise };
    return promise;
  }

  private async loadSavedSearchList(
    page: number,
    limit: number,
    withStats: boolean,
  ) {
    const offset = (page - 1) * limit;
    try {
      const response = await searchService.fetchSavedSearch({
        limit,
        offset,
        withStats: withStats ? 1 : undefined,
      });
      runInAction(() => {
        const rows: any[] = response.data || [];
        this.savedSearchRaw = rows;
        this.list = rows.map((item: any) => new SavedSearch(item));
        this.savedSearchTotal = response.total ?? rows.length;
        this.savedSearchLoaded = true;
        this.savedSearchHasStats = withStats;
      });
    } catch (e) {
      // the endpoint 400s on an out-of-range limit/offset; leaving the latch
      // unset lets the next caller retry instead of reading an empty list
      console.error('Failed to load saved searches:', e);
    }
  }

  /** Load once per project — resolves immediately when the list is already in
      state, and joins the in-flight request otherwise. Counts are only returned
      when asked for, so a cached stats-less list is refetched for a caller that
      needs them. */
  ensureSavedSearchList(withStats = false): Promise<void> {
    if (this.savedSearchLoaded && (!withStats || this.savedSearchHasStats)) {
      return Promise.resolve();
    }
    return this.fetchSavedSearchList(1, SAVED_SEARCH_PAGE_SIZE, withStats);
  }

  /** Mark the cached list stale so the next `ensureSavedSearchList` refetches. */
  invalidateSavedSearchList() {
    this.savedSearchLoaded = false;
    this.savedSearchHasStats = false;
  }

  async loadSharedSearch(searchId: string): Promise<void> {
    try {
      const response = await searchService.getSavedSearch(searchId);
      if (response) {
        const filtersData =
          response.data?.filters || response.filter?.filters || [];
        const searchData = response.data;
        const filters = filterStore.processFiltersFromData(filtersData);

        this.edit({
          filters: filters as any,
          startDate: searchData?.startTimestamp || this.instance.startDate,
          endDate: searchData?.endTimestamp || this.instance.endDate,
          sort: searchData?.sort || this.instance.sort,
          order: searchData?.order || this.instance.order,
          eventsOrder: searchData?.eventsOrder || this.instance.eventsOrder,
          rangeName: 'CUSTOM_RANGE',
          rangeValue: 'CUSTOM_RANGE',
        });

        this.currentPage = 1;
        await this.fetchSessions(true);
      }
    } catch (error) {
      console.error('Failed to load shared search:', error);
      throw error;
    }
  }

  edit(instance: Partial<Search>) {
    this.instance = new Search({ ...this.instance, ...instance });
    this.currentPage = 1;
  }

  editSavedSearch(instance: Partial<SavedSearch>) {
    this.savedSearch = new SavedSearch(
      Object.assign(this.savedSearch.toData(), instance),
    );
  }

  apply(filter: any, fromUrl: boolean) {
    if (fromUrl) {
      this.instance = new Search(filter);
    } else {
      this.instance = new Search({ ...this.instance.toData(), ...filter });
    }
    this.currentPage = 1;
  }

  applyFilter(filter: any) {
    this.apply(filter, false);
  }

  fetchFilterSearch(params: any) {
    this.loadingFilterSearch = true;

    searchService
      .fetchFilterSearch(params)
      .then((response: any[]) => {
        this.filterSearchList = response.reduce(
          (
            acc: Record<string, { projectId: number; value: string }[]>,
            item: any,
          ) => {
            const { projectId, type, value } = item;
            if (!acc[type]) acc[type] = [];
            acc[type].push({ projectId, value });
            return acc;
          },
          {},
        );
      })
      .catch((error: any) => {
        console.error('Error fetching filter search:', error);
      })
      .finally(() => {
        this.loadingFilterSearch = false;
      });
  }

  updateCurrentPage(page: number, force = false) {
    this.currentPage = page;
    void this.fetchSessions(force);
  }

  setPage(page: number) {
    this.currentPage = page;
  }

  nextPage = () => {
    this.currentPage += 1;
    return this.currentPage;
  };

  updateLatestSessionCount(count: number = 0) {
    this.latestSessionCount = count;
  }

  setActiveTab(tab: string) {
    runInAction(() => {
      this.activeTab = TAB_MAP[tab];
    });
  }

  resetTags = () => {
    this.activeTags = ['all'];
  };

  toggleTag(tag?: string) {
    if (!tag) {
      this.activeTags = [];
      void this.fetchSessions(true);
    } else {
      this.activeTags = [tag];
      void this.fetchSessions(true);
    }
  }

  async removeSavedSearch(id: string): Promise<void> {
    await searchService.deleteSavedSearch(id);
    this.detachSegment();
    await this.fetchSavedSearchList();
  }

  /** Saves the search as a share link and returns its id; the search keeps
      whatever segment it is. */
  async saveAsShare(): Promise<string | undefined> {
    const searchData = this.instance.toSearch();

    // Ensure all filters have required fields
    const ensureFilterFields = (filter: any): any => {
      return {
        ...filter,
        name: filter.name || filter.type,
        type: filter.type || filter.name,
        dataType: filter.dataType || 'string',
        operator: filter.operator || 'is',
        propertyOrder:
          filter.propertyOrder || (filter.isEvent ? 'then' : 'and'),
        filters: Array.isArray(filter.filters)
          ? filter.filters.map(ensureFilterFields)
          : [],
      };
    };

    // Construct payload for sharing
    const payload: any = {
      name: null, // Shared searches don't need a name
      isPublic: false,
      isShare: true, // Mark as shared
      data: {
        filters: searchData.filters.map(ensureFilterFields),
        startTimestamp: searchData.startTimestamp,
        endTimestamp: searchData.endTimestamp,
        sort: searchData.sort || 'startTs',
        order: searchData.order || 'desc',
        eventsOrder: searchData.eventsOrder || 'then',
        limit: searchData.limit || 10,
        page: searchData.page || 1,
      },
    };

    const savedSearchResponse = await searchService.saveSavedSearch(payload);
    return savedSearchResponse?.searchId;
  }

  /** Busy until the list shows the result, so the form can say so. */
  async save(id?: string | null, rename = false): Promise<void> {
    this.isSaving = true;
    try {
      await this.saveSearch(id, rename);
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  }

  private async saveSearch(id?: string | null, rename = false): Promise<void> {
    const searchData = this.instance.toSearch();
    const instance = this.savedSearch.toData();

    // Determine if updating existing or creating new
    // Use provided id, or fall back to current savedSearch.searchId
    const searchId = id || this.savedSearch.searchId;
    const isNew = !searchId;

    // Serialize filters - they come from toSearch() which already has them in JSON format
    const filtersToSave = rename
      ? instance.data?.filters || instance.filter?.filters || []
      : searchData.filters;

    // Ensure all filters have required fields
    const ensureFilterFields = (filter: any): any => {
      return {
        ...filter,
        name: filter.name || filter.type,
        type: filter.type || filter.name,
        dataType: filter.dataType || 'string',
        operator: filter.operator || 'is',
        propertyOrder:
          filter.propertyOrder || (filter.isEvent ? 'then' : 'and'),
        filters: Array.isArray(filter.filters)
          ? filter.filters.map(ensureFilterFields)
          : [],
      };
    };

    // Construct payload matching the new API structure
    const payload: any = {
      name: instance.name || null,
      isPublic: instance.isPublic || false,
      isShare: instance.isShare || false,
      data: {
        filters: filtersToSave.map(ensureFilterFields),
        startTimestamp: searchData.startTimestamp,
        endTimestamp: searchData.endTimestamp,
        sort: searchData.sort || 'startTs',
        order: searchData.order || 'desc',
        eventsOrder: searchData.eventsOrder || 'then',
        limit: searchData.limit || 10,
        page: searchData.page || 1,
      },
    };

    const savedSearchResponse = isNew
      ? await searchService.saveSavedSearch(payload)
      : await searchService.updateSavedSearch(searchId!, payload);

    // Update the current savedSearch with the response data
    if (savedSearchResponse) {
      this.savedSearch = new SavedSearch({
        ...instance,
        searchId: savedSearchResponse.searchId || searchId,
        name: instance.name,
        isPublic: instance.isPublic,
        isShare: instance.isShare,
        data: payload.data,
      });
      if (!rename) this.segmentRules = this.rulesKey;
    }

    // Refresh the list to show the latest saved searches
    await this.fetchSavedSearchList();
    // Refresh filters so the new/updated segment appears in OmniSearch
    void filterStore.fetchFilters(String(projectStore.activeSiteId), true);
  }

  clearList() {
    this.list = [];
    this.savedSearchRaw = [];
    this.savedSearchLoaded = false;
    this.savedSearchRequest = null;
  }

  clearSearch() {
    const { instance } = this;
    this.edit(
      new Search({
        rangeValue: instance.rangeValue,
        startDate: instance.startDate,
        endDate: instance.endDate,
        filters: [],
      }),
    );

    this.detachSegment();
    sessionStore.clearList();
    this.latestRequestTime = null;
    this.latestSessionCount = 0;
    this.listGeneration += 1;
  }

  /** Sessions matching the search that arrived since the list on screen loaded.
      Compared against that list's own total, so it waits for the list. */
  async checkForLatestSessionCount(): Promise<void> {
    if (
      this.searchInProgress ||
      sessionStore.loadingSessions ||
      this.latestRequestTime == null
    )
      return;
    const generation = this.listGeneration;
    try {
      let filter = this.instance.toSearch();
      filter = this.applyTagFilter(filter, this.activeTags);
      filter = this.applyDurationFilter(filter);

      // Set time filter if we have the latest request time
      if (this.latestRequestTime) {
        const period = Period({
          rangeName: CUSTOM_RANGE,
          start: this.latestRequestTime,
          end: Date.now(),
        });
        const timeRange: any = period.toJSON();
        filter.startDate = timeRange.startDate;
        filter.endDate = timeRange.endDate;
      }

      // Only need the total count, not actual records
      filter.limit = 1;
      filter.page = 1;

      const response = await sessionService.getSessions(filter);

      runInAction(() => {
        // a search started or landed meanwhile: it answers for itself
        if (this.searchInProgress || generation !== this.listGeneration) return;
        if (response?.total && response.total > sessionStore.total) {
          this.latestSessionCount = response.total - sessionStore.total;
        } else {
          this.latestSessionCount = 0;
        }
      });
    } catch (error) {
      console.error('Failed to check for latest session count:', error);
    }
  }

  addFilterOnce = (filter: any) => {
    const isPresent = this.instance.filters.find(
      (f) => f.name === filter.name && f.isEvent === filter.isEvent,
    );
    if (isPresent) {
      return;
    }
    this.addFilter(filter);
  };

  getFilterDefaults = (filter: any) => {
    if (filter.isEvent && (!filter.filters || filter.filters.length === 0)) {
      filterStore.getEventFilters(filter.id).then((props) => {
        filter.filters = props?.filter((prop) => prop.defaultProperty);
      });
    }

    return filter;
  };

  addFilter = (filter: any) => {
    filter = this.getFilterDefaults(filter);
    if (
      !filterStore.checkDefaultSubfilters(filter) &&
      filter.isEvent &&
      (!filter.filters || filter.filters.length === 0)
    ) {
      filterStore.getEventFilters(filter.id).then((props) => {
        filter.filters = props?.filter((prop) => prop.defaultProperty);
      });
    }

    if (!filter.isEvent) {
      const isPresent = this.instance.filters.find(
        (f) => f.name === filter.name && f.isEvent === false,
      );
      if (isPresent) {
        return;
      }
    }

    filter.value = checkFilterValue(filter.value);
    filter.operator = filter.operator || 'is';
    filter.filters = filter.filters
      ? filter.filters.map((subFilter: any) => ({
          ...subFilter,
          value: checkFilterValue(subFilter.value),
        }))
      : null;
    // a new array: PrivateRoutes refetches when the filters array changes
    const inst = this.instance.toData();
    inst.filters = [...inst.filters, filter];
    this.instance = new Search(inst);
    this.currentPage = 1;
  };

  moveFilter(draggedIndex: number, newPosition: number) {
    const newFilters = this.instance.filters.slice();
    const [removed] = newFilters.splice(draggedIndex, 1);
    newFilters.splice(newPosition, 0, removed);

    this.instance = new Search({
      ...this.instance.toData(),
      filters: newFilters,
    });
  }

  addFilterByKeyAndValue(
    key: any,
    value: any,
    operator?: string,
    sourceOperator?: string,
    source?: string,
  ) {
    const defaultFilter = { ...filtersMap[key] };

    defaultFilter.value = value;

    if (operator) {
      defaultFilter.operator = operator;
    }
    if (defaultFilter.hasSource && source && sourceOperator) {
      defaultFilter.sourceOperator = sourceOperator;
      defaultFilter.source = source;
    }

    this.addFilter(defaultFilter);
  }

  updateSearch = (search: Partial<Search>) => {
    this.instance = Object.assign(this.instance, search);
  };

  updateFilter = (index: number, search: Partial<Filter>) => {
    search = this.getFilterDefaults(search);
    const isReplacingFilter = this.instance.filters[index].name !== search.name;
    const newFilters = [...this.instance.filters];

    // @ts-ignore is not partial in such case
    newFilters[index] = isReplacingFilter
      ? search
      : {
          ...newFilters[index],
          ...search,
        };

    this.instance = new Search({
      ...this.instance.toData(),
      filters: newFilters,
    });
  };

  removeFilter = (index: number) => {
    const newFilters = [...this.instance.filters];
    newFilters.splice(index, 1);

    this.instance = new Search({
      ...this.instance.toData(),
      filters: newFilters,
    });
  };

  setScrollPosition = (y: number) => {
    this.scrollY = y;
  };

  async fetchSessions(
    force: boolean = false,
    bookmarked: boolean = false,
  ): Promise<void> {
    const superseded = Boolean(this.searchAbort);
    this.searchAbort?.abort();
    const controller = new AbortController();
    this.searchAbort = controller;

    let filter = this.instance.toSearch();
    filter = this.applyTagFilter(filter, this.activeTags);
    filter = this.applyDurationFilter(filter);
    this.latestList = [];
    this.searchInProgress = true;
    try {
      const loaded = await sessionStore.fetchSessions(
        {
          ...pickSearchRequestFields(filter),
          page: this.currentPage,
          limit: getRowsPerPage('sessions'),
          bookmarked: bookmarked ? true : undefined,
        },
        // SessionStore skips a search whose filters match the persisted ones —
        // including the ones the aborted request just wrote.
        force || superseded,
        controller.signal,
      );
      if (loaded && !controller.signal.aborted) {
        // the list on screen now holds everything up to this request
        runInAction(() => {
          this.latestRequestTime = filter.startDate;
          this.latestSessionCount = 0;
          this.listGeneration += 1;
        });
      }
    } catch (e) {
      if (!controller.signal.aborted) throw e;
    } finally {
      if (this.searchAbort === controller) {
        runInAction(() => {
          this.searchAbort = null;
          this.searchInProgress = false;
        });
      }
    }
  }

  /** When the newest session these filters match in the past 30 days was
      recorded: what an empty window can be widened to. */
  async fetchLastSessionAt(): Promise<number | null> {
    let filter = this.instance.toSearch();
    filter = this.applyTagFilter(filter, this.activeTags);
    filter = this.applyDurationFilter(filter);
    const now = Date.now();
    try {
      const data = await sessionService.getSessions({
        ...pickSearchRequestFields(filter),
        startTimestamp: now - 30 * 86400_000,
        endTimestamp: now,
        sort: 'startTs',
        order: 'desc',
        page: 1,
        limit: 1,
      });
      return data?.sessions?.[0]?.startTs ?? null;
    } catch {
      return null;
    }
  }

  private applyTagFilter(filter: any, activeTags: string[]): any {
    if (!activeTags?.length || activeTags[0] === 'all') {
      return filter;
    }

    const tagFilter = filterStore.findEvent({
      name: 'issue',
      isEvent: false,
      autoCaptured: true,
    });

    if (!tagFilter) {
      console.error('Tag filter not found for issue');
      return filter;
    }

    // a group (Broken, Frustrated...) stands for any of its kinds
    tagFilter.value = [activeTags[0]];

    return {
      ...filter,
      filters: [...filter.filters, tagFilter.toJson()],
    };
  }

  private applyDurationFilter(filter: any): any {
    if (filter.filters.some((f: any) => f.name === FilterKey.DURATION)) {
      return filter;
    }

    const { durationFilter } = settingsStore.sessionSettings;

    if (!durationFilter?.count || durationFilter.count <= 0) {
      return filter;
    }

    const multiplier = durationFilter.countType === 'sec' ? 1000 : 60000;
    const amount = durationFilter.count * multiplier;
    const value =
      durationFilter.operator === '<'
        ? [amount.toString()]
        : [null, amount.toString()];

    const durationFilterConfig = {
      autoCaptured: true,
      dataType: 'number',
      name: FilterKey.DURATION,
      value,
      operator: '=',
    };

    return {
      ...filter,
      filters: [...filter.filters, durationFilterConfig],
    };
  }
}

export default SearchStore;
